package com.crowpay.views.dialogs

import com.crowpay.LineItem
import com.crowpay.ScopeView
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.contractor
import com.crowpay.extensions.toLineItems
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.SignalingList
import com.crowpay.utils.renderDollars
import com.crowpay.utils.truncate
import com.crowpay.views.components.field2
import com.crowpay.views.components.leftExpandButton
import com.crowpay.views.components.scopeSetIcon
import com.crowpay.views.components.sectionIndentCol
import com.crowpay.views.theming.RemoveOutline
import com.crowpay.views.theming.RemoveOutlineSemantic
import com.crowpay.views.theming.RemoveToggleSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.boldSubTitle
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.title
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.debounce
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.reactive.withWrite
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.dialog
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.checkbox
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.dismissBackground
import com.lightningkite.kiteui.views.direct.gravity
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.padded
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.textInput
import com.lightningkite.kiteui.views.direct.toggleButton
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.forEach
import com.lightningkite.kiteui.views.italic
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.condition
import com.lightningkite.lightningdb.eq


class NewTasksFromSpecsDialog(
    val contractorId: UUID,
    val projectId: UUID,
    val withTasks: suspend (Map<ScopeView?, List<LineItem>>) -> Unit
) : Page {
    private val _specifications = shared {
        notNullSession().specifications.query(
            Query(
                condition { it.contractor eq contractorId },
                limit = Int.MAX_VALUE
            )
        )
    }

    val specifications = shared {
        _specifications()()
    }

    val selectedSpecs = SignalingList<UUID>()

    val search = Property("")
    val debouncedSearch = search.debounce(200)

    val filteredSpecs = shared {
        val specs = specifications()

        debouncedSearch()
            .takeUnless { it.isBlank() }
            ?.let { query ->
                val queried = specs
                    .filter {
                        it.group?.name?.contains(query, ignoreCase = true) == true ||
                                it.title.contains(query, ignoreCase = true) ||
                                it.description.contains(query, ignoreCase = true)
                    }

                val groups = queried.mapNotNull { it.group?.name }.toSet()
                val ids = queried.map { it._id }.toSet()

                specs.filter { it._id in ids || it.group?.name in groups }
            }
            ?: specs
    }

    val filteredSpecGroups = shared {
        filteredSpecs().groupBy { it.group }.toList()
    }

    override fun ViewWriter.render2(): ViewModifiable {
        return dismissBackground {
            centered - dialog - scrolls - stack {
                spacing = AppDimensions.fullIndent

                sizeConstraints(minWidth = 40.rem, minHeight = 30.rem, maxHeight = 60.rem) - scrolls - col {
                    spacing = AppDimensions.sectionIndent
                    title("New Tasks From Specifications")

                    field2("Search") {
                        textInput {
                            hint = "Specification Group, Title, Description"
                            content bind search
                        }
                    }

                    boldSubTitle("Available Specifications")
                    scrolls - col {
                        forEach(filteredSpecGroups) { (group, specs) ->
                            val groupSelected = shared {
                                selectedSpecs().containsAll(specs.map { it._id })
                            }.withWrite { selected ->
                                if (selected) selectedSpecs += specs.map { it._id }
                                else selectedSpecs -= specs.map { it._id }
                            }

                            lightSection - (RemoveOutlineSemantic + RemoveToggleSemantic).onNext - compact - row {
                                spacing = 0.rem
                                stack {
                                    gravity(Align.Start, Align.Center) - checkbox {
                                        enabled = group != null
                                        visible = group != null
                                        checked bind groupSelected
                                    }
                                }
                                expanding - toggleButton {
                                    enabled = group != null
                                    checked bind groupSelected
                                    compact - row {
                                        if (group?.isSet == true) centered - scopeSetIcon(1.3.rem)
                                        centered - bodyBold {
                                            content = group?.name ?: "Individual Specifications"
                                        }
                                        expanding - centered - italic - smallBody(group?.description ?: "")
                                        centered - body {
                                            align = Align.End
                                            visible = specs.sumOf { it.price ?: 0L } > 0
                                            content = specs.sumOf { it.price ?: 0L }.renderDollars()
                                        }
                                    }
                                }
                            }

                            col {
                                for (spec in specs) {
                                    col {
                                        spacing = 0.rem
                                        val expanded = Property(false)
                                        RemoveOutline.onNext - compact - row {
                                            stack {
                                                gravity(Align.Start, Align.Center) - checkbox {
                                                    visible = !spec.isSet
                                                    checked bind selectedSpecs.containsWritable(spec._id)
                                                }
                                            }
                                            expanding - leftExpandButton(expanded) {
                                                body {
                                                    ::content {
                                                        spec.title
                                                    }
                                                }
                                                expanding - centered - italic - smallBody(spec.description.truncate(50))

                                                if (spec.price.let { it != null && it > 0 }) centered - smallBody(spec.price.renderDollars())
                                            }
                                        }
                                        sectionIndentCol {
                                            padded - onlyWhen { expanded() } - body(spec.description)
                                            greySeparator()
                                        }
                                    }
                                }
                            }
                        }
                    }
                    expanding - body {
                        ::exists { filteredSpecs().isEmpty() }
                        content = "No Specifications match the current search query"
                    }
                    space()
                    row {
                        secondaryButton - button {
                            specCenteredText("Discard")
                            onClick("Discard Scope View") { dialogScreenNavigator.dismiss() }
                        }
                        primaryButton - button {
                            ::enabled { selectedSpecs().isNotEmpty() }
                            specCenteredText {
                                "Create ${selectedSpecs().size} New Items"
                            }

                            onClick("Create New Items") {
                                val selected = selectedSpecs.toSet()
                                withTasks(
                                    specifications().filter { it._id in selected }.toLineItems(projectId)
                                )
                                dialogScreenNavigator.dismiss()
                            }
                        }
                    }
                }

            }
        }
    }
}
